import React from 'react';
import { Link } from 'react-router-dom';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { ChevronDown, Shield, Phone, Mail } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const PrivacyPolicyPage = () => {
  // Set document meta tags
  useDocumentMeta({
    title: 'Privacy Policy | Kalamata Airport Taxi',
    description: 'Kalamata Airport Taxi privacy policy. Learn how we collect, use, and protect your personal information when using our transfer services.',
    canonical: 'https://kalamataairporttaxi.com/privacy'
  });

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Inter', sans-serif" }}>
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative min-h-[40vh] flex items-center bg-[#2d232e] overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute inset-0" style={{
              backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
              backgroundSize: '40px 40px'
            }} />
          </div>
          
          {/* Decorative Elements */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#36827f]/20 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#36827f]/10 rounded-full blur-3xl" />

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative pt-32 pb-16 w-full">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm text-white/50 mb-8">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <ChevronDown className="h-4 w-4 rotate-[-90deg]" />
              <span className="text-[#36827f]">Privacy Policy</span>
            </nav>

            <div className="flex items-center gap-5">
              <div className="w-16 h-16 rounded-xl bg-[#36827f]/20 flex items-center justify-center">
                <Shield className="h-8 w-8 text-[#36827f]" />
              </div>
              <div>
                <h1 className="text-3xl lg:text-4xl font-bold text-white">Privacy Policy</h1>
                <p className="text-white/50 mt-1">Last updated: January 2026</p>
              </div>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            
            {/* Summary Box */}
            <div className="bg-[#36827f]/10 border border-[#36827f]/20 rounded-2xl p-6 mb-12">
              <p className="text-[#2d232e] m-0">
                <strong>Summary:</strong> We collect only essential information needed to provide our transfer services. 
                We never sell your data and only share it with necessary service providers to complete your booking.
              </p>
            </div>

            <div className="space-y-10">
              
              {/* Section 1 */}
              <div>
                <h2 className="text-xl font-bold text-[#2d232e] mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-[#36827f] text-white flex items-center justify-center text-sm font-bold">1</span>
                  Information We Collect
                </h2>
                <p className="text-gray-600 mb-4">When using our services, we gather the following data:</p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2">
                  <li><strong>Contact Details:</strong> Your name, email address, and phone number</li>
                  <li><strong>Reservation Information:</strong> Pickup and drop-off locations, travel dates, times, and passenger count</li>
                  <li><strong>Payment Details:</strong> Card information (handled securely through Stripe)</li>
                  <li><strong>Travel Data:</strong> Flight numbers (when provided), and any special requests</li>
                  <li><strong>Correspondence:</strong> Messages you send via our contact forms</li>
                </ul>
              </div>

              {/* Section 2 */}
              <div>
                <h2 className="text-xl font-bold text-[#2d232e] mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-[#36827f] text-white flex items-center justify-center text-sm font-bold">2</span>
                  How We Use Your Information
                </h2>
                <p className="text-gray-600 mb-4">Your information helps us to:</p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2">
                  <li>Handle and manage your transfer reservations</li>
                  <li>Keep you informed about your bookings</li>
                  <li>Deliver confirmation emails and pickup reminders</li>
                  <li>Process your payments safely</li>
                  <li>Enhance our services and your overall experience</li>
                  <li>Address your questions and support needs</li>
                </ul>
              </div>

              {/* Section 3 */}
              <div>
                <h2 className="text-xl font-bold text-[#2d232e] mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-[#36827f] text-white flex items-center justify-center text-sm font-bold">3</span>
                  Information Sharing
                </h2>
                <p className="text-gray-600 mb-4">We share your data only with:</p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2">
                  <li><strong>Our Drivers:</strong> So they can provide your transfer (name, pickup location, time)</li>
                  <li><strong>Payment Provider:</strong> Stripe handles payments securely on our behalf</li>
                  <li><strong>Email Provider:</strong> Resend sends transactional emails for us</li>
                </ul>
                <div className="bg-gray-50 rounded-xl p-4 mt-4">
                  <p className="text-gray-700 m-0 font-medium">
                    We do not sell, rent, or share your personal data with third parties for marketing.
                  </p>
                </div>
              </div>

              {/* Section 4 */}
              <div>
                <h2 className="text-xl font-bold text-[#2d232e] mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-[#36827f] text-white flex items-center justify-center text-sm font-bold">4</span>
                  Data Security
                </h2>
                <p className="text-gray-600 mb-4">
                  We take appropriate steps to keep your personal information safe:
                </p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2">
                  <li>SSL encryption protects all data transfers</li>
                  <li>Payments are processed through PCI-compliant Stripe</li>
                  <li>Our database has restricted access and secure storage</li>
                  <li>We conduct regular security assessments and updates</li>
                </ul>
              </div>

              {/* Section 5 */}
              <div>
                <h2 className="text-xl font-bold text-[#2d232e] mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-[#36827f] text-white flex items-center justify-center text-sm font-bold">5</span>
                  Data Retention
                </h2>
                <p className="text-gray-600 mb-4">
                  We keep your booking records for 3 years following your last reservation for:
                </p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2">
                  <li>Meeting legal and tax obligations</li>
                  <li>Resolving any disputes that may arise</li>
                  <li>Analyzing and improving our services</li>
                </ul>
                <p className="text-gray-600 mt-4">
                  You can ask us to delete your data at any time by getting in touch.
                </p>
              </div>

              {/* Section 6 */}
              <div>
                <h2 className="text-xl font-bold text-[#2d232e] mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-[#36827f] text-white flex items-center justify-center text-sm font-bold">6</span>
                  Your Rights (GDPR)
                </h2>
                <p className="text-gray-600 mb-4">Under GDPR, you are entitled to:</p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2">
                  <li><strong>Access:</strong> Obtain a copy of the personal data we hold about you</li>
                  <li><strong>Rectification:</strong> Have inaccurate information corrected</li>
                  <li><strong>Erasure:</strong> Ask for your personal data to be deleted</li>
                  <li><strong>Portability:</strong> Get your data in a transferable format</li>
                  <li><strong>Object:</strong> Challenge certain uses of your data</li>
                </ul>
                <p className="text-gray-600 mt-4">
                  To use these rights, email us at <a href="mailto:info@kalamataairporttaxi.com" className="text-[#36827f] hover:underline">info@kalamataairporttaxi.com</a>
                </p>
              </div>

              {/* Section 7 */}
              <div>
                <h2 className="text-xl font-bold text-[#2d232e] mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-[#36827f] text-white flex items-center justify-center text-sm font-bold">7</span>
                  Cookies
                </h2>
                <p className="text-gray-600 mb-4">
                  Our website uses only essential cookies to:
                </p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2">
                  <li>Keep your session active while you browse</li>
                  <li>Remember your booking preferences</li>
                  <li>Maintain website security</li>
                </ul>
                <p className="text-gray-600 mt-4">
                  We don't use tracking or advertising cookies.
                </p>
              </div>

              {/* Section 8 */}
              <div>
                <h2 className="text-xl font-bold text-[#2d232e] mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-[#36827f] text-white flex items-center justify-center text-sm font-bold">8</span>
                  Changes to This Policy
                </h2>
                <p className="text-gray-600">
                  This privacy policy may be updated periodically. When we make significant changes, 
                  we'll post the revised version here and update the "Last updated" date.
                </p>
              </div>

              {/* Section 9 - Contact */}
              <div>
                <h2 className="text-xl font-bold text-[#2d232e] mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-[#36827f] text-white flex items-center justify-center text-sm font-bold">9</span>
                  Contact Us
                </h2>
                <p className="text-gray-600 mb-4">
                  If you have questions about this Privacy Policy, please reach out:
                </p>
                <div className="bg-gray-50 rounded-2xl p-6">
                  <p className="font-bold text-[#2d232e] text-lg mb-4">Kalamata Airport Taxi</p>
                  <div className="space-y-3">
                    <a href="tel:+306932421170" className="flex items-center gap-3 text-gray-600 hover:text-[#36827f] transition">
                      <div className="w-10 h-10 rounded-lg bg-[#36827f]/10 flex items-center justify-center">
                        <Phone className="w-5 h-5 text-[#36827f]" />
                      </div>
                      <span>+30 693 242 1170</span>
                    </a>
                    <a href="mailto:info@kalamataairporttaxi.com" className="flex items-center gap-3 text-gray-600 hover:text-[#36827f] transition">
                      <div className="w-10 h-10 rounded-lg bg-[#36827f]/10 flex items-center justify-center">
                        <Mail className="w-5 h-5 text-[#36827f]" />
                      </div>
                      <span>info@kalamataairporttaxi.com</span>
                    </a>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;
